import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Glucose,
  Cardiology,
  Stethoscope,
  MonitorWeight,
  Restaurant,
  WaterFull,
  Bed,
  DirectionsWalk,
  Medication,
  MenstrualHealth,
  Exercise,
  Add,
  Reminder,
  Flag,
  ActivityZone,
} from "@material-symbols-svg/react";
import Layout from "src/components/app/layout/Layout";
import Tabs from "src/components/app/TabsV2";
import VitalsList from "src/components/app/insights/vitals/VitalsList";
import InsightComponentVital from "src/components/app/insights/InsightComponentVital";
import InsightComponentHealth from "src/components/app/insights/InsightComponentHealth";
import InsightComponentDiet from "src/components/app/insights/InsightComponentDiet";
import SymptomsChart from "src/components/app/insights/symptoms/SymptomsChart";
import SetGoalBottomSheet from "src/components/app/bottom-sheet/insights/SetGoalBottomSheet";
import SetRemindetBottomSheet from "src/components/app/bottom-sheet/insights/SetRemindetBottomSheet";
import AddMedicationBottomSheet from "src/components/app/bottom-sheet/insights/AddMedicationBottomSheet";
import supportPrograms from "src/data/app/programs";
import { LOGGERS_TYPES } from "src/constants/loggers";
import { bindQueryParams } from "src/helpers/tools";
import { Meh } from "lucide-react";
import InsightComponentGlucose from "src/components/app/insights/InsightComponentGlucose";
import { sendStatusBarColor } from "src/helpers/common";

const healthTabSubMenuMap: Record<
  string,
  {
    icon: React.ReactNode;
    color: string;
    backgroundColor: string;
  }
> = {
  Diet: {
    icon: <Restaurant className="w-6 h-6" />,
    color: "#EA5803",
    backgroundColor: "#FEF3E8",
  },
  Droplets: {
    icon: <WaterFull className="w-6 h-6" />,
    color: "#2563EB",
    backgroundColor: "#EFF6FF",
  },
  Sleep: {
    icon: <Bed className="w-6 h-6" />,
    color: "#9333EA",
    backgroundColor: "#F5F3FF",
  },
  Steps: {
    icon: <DirectionsWalk className="w-6 h-6" />,
    color: "#ef4444",
    backgroundColor: "#fee2e2",
  },
  Fatigue: {
    icon: <Meh className="w-6 h-6" />,
    color: "#8b5cf6",
    backgroundColor: "#f5f3ff",
  },
  Exercise: {
    icon: <Exercise className="w-6 h-6" />,
    color: "#3b82f6",
    backgroundColor: "#e0e7ff",
  },
  Medication: {
    icon: <Medication className="w-6 h-6" />,
    color: "#16a34a",
    backgroundColor: "#dcfce7",
  },
  Menstruation: {
    icon: <MenstrualHealth className="w-6 h-6" />,
    color: "#f472b6",
    backgroundColor: "#f9a8d4",
  },
};

type Tab = {
  id: string;
  title: string;
  icon: React.ReactNode;
  subTabs?: SubTab[];
};

type SubTab = {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  backgroundColor: string;
};

const InsightsScreen: React.FC = () => {
  const [tabs, setTabs] = useState<Tab[]>([
    { id: "health", title: "Health", icon: <Glucose className="w-6 h-6" /> },
    {
      id: "vitals",
      title: "Vitals",
      icon: <Cardiology className="w-6 h-6" />,
      subTabs: [
        //   {
        //     id: "lab-tests",
        //     name: "Lab Tests",
        //     icon: <LabPanel className="w-6 h-6" />,
        //     color: "#2663EB",
        //     backgroundColor: "#E8F3FE",
        //   },
        {
          id: "body-composition",
          name: "Body Composition",
          icon: <MonitorWeight className="w-6 h-6" />,
          color: "#16A349",
          backgroundColor: "#E7FFF3",
        },
        //   {
        //     id: "cardiovasular",
        //     name: "Cardiovasular",
        //     icon: <Cardiology className="w-6 h-6" />,
        //     color: "#DC2627",
        //     backgroundColor: "#FFE8E8",
        //   },
        {
          id: "glucose",
          name: "Glucose",
          icon: <Glucose className="w-6 h-6" />,
          color: "#9333EA",
          backgroundColor: "#F3E8FF",
        },
      ],
    },
    {
      id: "symptoms",
      title: "Symptoms",
      icon: <Stethoscope className="w-6 h-6" />,
    },
  ]);
  const [showGoalSheet, setShowGoalSheet] = useState(false);
  const [showReminderSheet, setShowReminderSheet] = useState(false);
  const [showAddMedicationSheet, setShowAddMedicationSheet] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"] as string;

  const queryParams = new URLSearchParams(window.location.search);
  console.log(
    "queryParams",
    queryParams,
    queryParams.get("tab"),
    queryParams.get("sub-tab"),
  );
  const activeTab = queryParams.get("tab") as string;
  const activeSubTab = queryParams.get("sub-tab") as string;

  const subTabs = tabs.find((t) => t.id === activeTab)?.subTabs || [];
  const activeSubTabObj = subTabs?.find((subTab) => subTab.id === activeSubTab);

  useEffect(() => {
    getHealthTabSubTabs();
  }, [supportProgramKey]);

  // TEST WIRING: report this screen's page background as the native status
  // bar color while it's mounted.
  useEffect(() => {
    const background =
      supportPrograms[supportProgramKey as keyof typeof supportPrograms]
        ?.theme?.colors?.background ?? "#ffffff";

    return sendStatusBarColor(background, "dark-content");
  }, [supportProgramKey]);

  useEffect(() => {
    if (!activeTab) {
      navigate(`/app/${supportProgramKey}/insights-screen?tab=${tabs[0].id}`);
      return;
    } else {
      if (!activeSubTab) {
        const subTab = subTabs?.[0]?.id;
        navigate(
          `/app/${supportProgramKey}/insights-screen?tab=${activeTab}${
            subTab ? `&sub-tab=${subTab}` : ""
          }`,
        );
        return;
      }
    }
  }, [activeTab, activeSubTab]);

  const getHealthTabSubTabs = () => {
    const config =
      supportPrograms[supportProgramKey as keyof typeof supportPrograms];
    const screenName = "insights-screen";
    const appScreens = config?.app?.screens;
    const screenData = appScreens?.[screenName as keyof typeof appScreens];

    const healthTabSubTabs = (screenData as any)?.logs?.map((log: any) => ({
      ...log,
      icon: healthTabSubMenuMap[log.icon]?.icon || (
        <ActivityZone className="w-6 h-6" />
      ), // fallback icon if not found
      color: healthTabSubMenuMap[log.icon]?.color,
      backgroundColor: healthTabSubMenuMap[log.icon]?.backgroundColor,
      name: log.title,
      id: log.id,
    })) as SubTab[];

    console.log("healthTabSubTabs", (screenData as any)?.logs);

    setTabs(
      (prevTabs) =>
        prevTabs.map((tab) =>
          tab.id === "health" ? { ...tab, subTabs: healthTabSubTabs } : tab,
        ) as Tab[],
    );
  };

  const handleLogNowClick = (loggerId: string) => {
    const loggerType = LOGGERS_TYPES[loggerId as keyof typeof LOGGERS_TYPES];
    const url = bindQueryParams(
      `/app/${supportProgramKey}/health-assistant-screen`,
      {
        loggerType,
      },
    );
    navigate(url);
  };

  const renderHeader = () => {
    return (
      <>
        <Tabs
          className="m-4"
          tabs={tabs}
          activeTab={activeTab}
          onChange={(tab) => {
            const subTab = tabs.find((t) => t.id === tab.id)?.subTabs;
            if (subTab) {
              navigate(
                `/app/${supportProgramKey}/insights-screen?tab=${tab.id}&sub-tab=${subTab[0].id}`,
              );
            } else {
              navigate(
                `/app/${supportProgramKey}/insights-screen?tab=${tab.id}`,
              );
            }
          }}
        />

        {subTabs?.length > 0 && (
          <>
            <VitalsList
              className="mx-4 pb-4"
              vitals={subTabs}
              activeVital={activeSubTab}
              onVitalClick={(vitalId) => {
                navigate(
                  `/app/${supportProgramKey}/insights-screen?tab=${activeTab}&sub-tab=${vitalId}`,
                );
              }}
            />
            <p className="text-center text-sm font-bold text-gray-800 absolute top-[155px] left-0 right-0">
              {activeSubTabObj?.name ?? ""}
            </p>
          </>
        )}
      </>
    );
  };

  return (
    <Layout
      supportProgramKey={supportProgramKey}
      showBottomBar={true}
      enableTheme={true}
      header={renderHeader()}
      backgroundLinearGradient={true}
      className="relative pt-[150px]"
      bodyClassName="mt-10"
    >
      {activeTab === "vitals" &&
        (activeSubTabObj?.id === "body-composition" ? (
          <InsightComponentVital
            insightType={activeTab}
            parameter={activeSubTab}
            barColor={activeSubTabObj?.color}
            icon={activeSubTabObj?.icon}
            supportProgramKey={supportProgramKey}
          />
        ) : activeSubTabObj?.id === "glucose" ? (
          <InsightComponentGlucose />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-sm text-gray-500">No data found!</div>
          </div>
        ))}

      {activeTab === "symptoms" && <SymptomsChart className="px-4" />}
      {activeTab === "health" &&
        (activeSubTab === "diet-logs" ? (
          <InsightComponentDiet
            insightType={activeTab}
            parameter={activeSubTab}
            barColor={activeSubTabObj?.color}
            icon={activeSubTabObj?.icon}
            supportProgramKey={supportProgramKey}
            options={[
              {
                icon: <Add className="w-5 h-5" />,
                label: "Log Now",
                onClick: () => handleLogNowClick(activeSubTab),
              },
              {
                icon: <Reminder className="w-5 h-5" />,
                label: "Set Reminder",
                onClick: () => setShowReminderSheet(true),
              },
              {
                icon: <Flag className="w-5 h-5" />,
                label: "Set Goal",
                onClick: () => setShowGoalSheet(true),
              },
            ]}
          />
        ) : (
          <InsightComponentHealth
            insightType={activeTab}
            parameter={activeSubTab}
            barColor={activeSubTabObj?.color}
            icon={activeSubTabObj?.icon}
            supportProgramKey={supportProgramKey}
            options={
              activeSubTab === "medication-logs"
                ? [
                    {
                      icon: <Medication className="w-5 h-5" />,
                      label: "Log Medication",
                      onClick: () => handleLogNowClick(activeSubTab),
                    },
                    {
                      icon: <Add className="w-5 h-5" />,
                      label: "Add Medication",
                      onClick: () => setShowAddMedicationSheet(true),
                    },
                    {
                      icon: <Reminder className="w-5 h-5" />,
                      label: "Set Reminder",
                      onClick: () => setShowReminderSheet(true),
                    },
                    {
                      icon: <Flag className="w-5 h-5" />,
                      label: "Set Goal",
                      onClick: () => setShowGoalSheet(true),
                    },
                  ]
                : [
                    {
                      icon: <Add className="w-5 h-5" />,
                      label: "Log Now",
                      onClick: () => handleLogNowClick(activeSubTab),
                    },
                    {
                      icon: <Reminder className="w-5 h-5" />,
                      label: "Set Reminder",
                      onClick: () => setShowReminderSheet(true),
                    },
                    {
                      icon: <Flag className="w-5 h-5" />,
                      label: "Set Goal",
                      onClick: () => setShowGoalSheet(true),
                    },
                  ]
            }
          />
        ))}
      <SetGoalBottomSheet
        show={showGoalSheet}
        handleClose={() => setShowGoalSheet(false)}
        type={activeSubTab}
      />
      <SetRemindetBottomSheet
        show={showReminderSheet}
        handleClose={() => setShowReminderSheet(false)}
        type={activeSubTab}
      />
      <AddMedicationBottomSheet
        show={showAddMedicationSheet}
        handleClose={() => setShowAddMedicationSheet(false)}
      />
    </Layout>
  );
};

export default InsightsScreen;
